/* eslint jsx-a11y/label-has-associated-control:0 */
/* eslint jsx-a11y/no-static-element-interactions:0 */

import React, { Component } from 'react';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import CONST from 'app/common/lib/Const';
import contextEvent from 'app/common/lib/ContextEvent';
import contextLogger from 'app/common/lib/ContextLogger';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import InlineStyle from 'app/common/react/components/InlineStyle';
import PropTypes from 'prop-types';
import pubSub from 'app/common/lib/PubSub';
import SearchBoxInput from 'app/common/react/components/growth/unauth/open/search/SearchBoxInput/SearchBoxInput';
import SearchTypeahead from 'app/common/react/components/growth/unauth/open/search/SearchTypeahead/SearchTypeahead';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import UnauthSearchBoxCSS from 'app/common/react/components/growth/unauth/header/UnauthHeader/UnauthSearchBoxCSS';
import urlUtil from 'app/common/lib/urlUtil';
import { Box, Icon } from 'gestalt';

const styles = {
  openSearchBoxWrapper: {
    flex: '1',
  },
  inputToken: {
    color: '#000',
    display: 'inline-block',
    margin: 0,
    overflow: 'hidden',
    width: '100%',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  inputLabel: {
    height: '40px',
    display: 'flex',
  },
  searchForm: {
    boxSizing: 'border-box',
    display: 'block',
    float: 'none',
    height: 'auto',
    marginLeft: '16px',
    marginRight: '16px',
    position: 'relative',
    textAlign: 'left',
    width: 'auto',
  },
  typeaheadField: {
    display: 'block',
    float: 'none',
    position: 'relative',
    width: '100%',
  },
  tokenizedInputWrapper: {
    backgroundColor: '#efefef',
    borderRadius: '4px',
    boxSizing: 'border-box',
    height: '40px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    marginLeft: '16px',
  },
  tokenizedInput: {
    marginLeft: '11px',
    overflow: 'hidden',
    flex: '1',
    display: 'flex',
    alignItems: 'center',
  },
  scrollWrapper: {
    overflow: 'hidden',
    flex: 1,
  },
  tokensWrapper: {
    cursor: 'text',
    float: 'left',
    minHeight: '1px',
    whiteSpace: 'nowrap',
    width: '100%',
    left: 0,
    overflow: 'visible',
    position: 'relative',
    transitionDuration: '.25s',
    transitionProperty: 'left',
  },
  overlay: {
    bottom: 0,
    left: 0,
    position: 'fixed',
    right: 0,
    top: '64px',
  },
  typeaheadOverlay: {
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  removeAll: {
    cursor: 'pointer',
    transition: 'display 1s linear',
    paddingRight: '9px',
    paddingBottom: '2px',
  },
};

export const QUERY_TERM_STATE_TYPE = {
  AUTO_COMPLETE_DEFAULT: 'query',
  AUTO_COMPLETE_HASHTAG: 'hashtag',
  AUTO_COMPLETE_RECENT_SEARCH: 'recent_query',
  AUTO_COMPLETE_TRENDING: 'trending',
  TYPO_AUTO_CORRECT_ORIGINAL: 'typo_auto_original',
  USER_INPUT: 'typed',
  SEARCH_GUIDE: 'add_refine',
};

function constructSingleQueryStateParam({ type, term, index }) {
  switch (type) {
    case QUERY_TERM_STATE_TYPE.USER_INPUT:
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_HASHTAG:
    case QUERY_TERM_STATE_TYPE.TYPO_AUTO_CORRECT_ORIGINAL:
      return `${term}|${type}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_DEFAULT:
      return `${term}|autocomplete|${index}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_RECENT_SEARCH:
      return `${term}|recentsearch|${index}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_TRENDING:
      return `${term}|trending|${index}`;
    case QUERY_TERM_STATE_TYPE.SEARCH_GUIDE:
      return `${term}|guide|word|${index}`;
    default:
      return undefined;
  }
}

function constructQueryStatesParams({ queryStates, guideAdded }) {
  if (!queryStates || queryStates.length === 0) {
    return '';
  }
  return (
    '&' +
    urlUtil.getQueryStringFromObject({
      term_meta: queryStates.map((s) => constructSingleQueryStateParam(s)),
      ...(guideAdded && {
        [QUERY_TERM_STATE_TYPE.SEARCH_GUIDE]: constructSingleQueryStateParam({
          ...guideAdded,
          type: QUERY_TERM_STATE_TYPE.SEARCH_GUIDE,
        }),
      }),
    })
  );
}

function queryToTerms(query) {
  if (query) {
    const terms = query.trim().split(/\s+/);
    const conjunction = terms.find((term) =>
      CONST.SEARCH_FILTER_CONJUNCTIONS.includes(term.toLowerCase()),
    );

    if (conjunction) {
      return [query];
    } else {
      return terms;
    }
  }
  return [];
}

function normalizeSearchQuery(query) {
  return (query || '').trim().replace(/\s+/g, ' ');
}

class SearchBox extends Component {
  static propTypes = {
    className: PropTypes.string,
    initialFocus: PropTypes.bool,
    initialText: PropTypes.string,
    onBlur: PropTypes.func,
    onQueryChange: PropTypes.func,
    hideRemoveAll: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    const originalQuery = this.getOriginalQuery();

    this.state = {
      currentBoost: '',
      currentScope: '',
      emptySuggestions: [],
      focused: props.initialFocus,
      hoverItem: undefined,
      query: originalQuery,
      queryStates: [],
      text: originalQuery || props.initialText || '',
      typeaheadSuggestions: [],
      windowHeight: undefined,
    };
  }

  getOriginalQuery() {
    const query = (window && window.location && window.location.search) || '';
    if (!query) {
      return '';
    }
    // Method for parsing search terms -> JSON object from here:
    // https://stackoverflow.com/questions/8648892/convert-url-parameters-to-a-javascript-object
    const search = query.substring(1);
    try {
      const queryObject = JSON.parse(
        '{"' + search.replace(/&/g, '","').replace(/=/g, '":"') + '"}',
        (key, value) => (key === '' ? value : decodeURIComponent(value)),
      );

      if (queryObject.q) {
        return queryObject.q;
      }
    } catch (e) {
      return '';
    }
    return '';
  }

  componentDidMount() {
    this.onResize();

    window.addEventListener('resize', this.onResize);

    this.subscribers.push(
      pubSub.instance.subscribe(
        PCONST.PUBSUB_CHANNEL_SITE,
        PCONST.PUBSUB_TOPIC_SEARCH_QUERY_UPDATED_FORCE_UPDATE,
        this.onSearchQueryUpdated,
      ),
    );
    this.subscribers.push(
      pubSub.instance.subscribe(
        PCONST.PUBSUB_CHANNEL_SITE,
        PCONST.PUBSUB_TOPIC_SEARCH_SCOPE_UPDATED,
        this.onSearchScopeUpdated,
      ),
    );
    this.subscribers.push(
      pubSub.instance.subscribe(
        PCONST.PUBSUB_CHANNEL_SITE,
        PCONST.PUBSUB_TOPIC_SEARCH_GUIDE_SELECTED,
        this.onSearchGuideSelected,
      ),
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const originalQuery = nextProps.initialText || this.getOriginalQuery();

    this.setState({
      currentBoost: '',
      text: originalQuery,
      query: originalQuery,
      currentScope: '',
      queryStates: [],
      hoverItem: undefined,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    this.subscribers.forEach((subscriber) => subscriber.unsubscribe());
    this.subscribers = [];
  }

  onResize = () => {
    this.setState({
      windowHeight: window.innerHeight,
    });
  };

  onSearchGuideSelected = (target, topic, message = {}) => {
    const { index, position, term = '', autoCorrectedQuery } = message;

    if (!term) {
      return;
    }

    let query = autoCorrectedQuery || this.state.query;
    if (position < 0) {
      query = term;
    } else {
      const shouldPrependTerm = !position;
      query = shouldPrependTerm ? `${term} ${query}` : `${query} ${term}`;
    }
    query = normalizeSearchQuery(query);
    const queryStates = this.state.queryStates;
    const nextQueryStates = [
      ...queryStates,
      {
        type: QUERY_TERM_STATE_TYPE.SEARCH_GUIDE,
        term,
        index,
      },
    ];

    this.setState({
      text: query,
      query,
      queryStates: nextQueryStates,
    });

    this.navigateWithData({
      query,
      queryStates,
      rs: 'guide',
      guideAdded: {
        term,
        index,
      },
    });
  };

  onSearchQueryUpdated = (channel, topic, enteredQuery = '') => {
    const query = normalizeSearchQuery(enteredQuery);

    this.setState({
      text: query,
      query,
    });
  };

  onSearchScopeUpdated = (target, topic, scope = 'pins') => {
    this.setState({ currentScope: scope });
  };

  // return new query states
  getNewQueryStates = ({ type, query, index }) => {
    switch (type) {
      case QUERY_TERM_STATE_TYPE.USER_INPUT:
      case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_HASHTAG:
      case QUERY_TERM_STATE_TYPE.TYPO_AUTO_CORRECT_ORIGINAL:
        return queryToTerms(query).map((term) => ({
          term,
          type,
        }));

      case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_RECENT_SEARCH:
      case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_TRENDING:
      case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_DEFAULT:
        return queryToTerms(query).map((term) => ({
          term,
          type,
          index, // index is resultIndex in the list
        }));

      default:
        return [];
    }
  };

  handleTypeaheadItemHover = (hoverItem) => {
    this.setState({
      hoverItem,
    });
  };

  handleTypeaheadItemSelected = (item, rawEnteredQuery) => {
    const enteredQuery = normalizeSearchQuery(rawEnteredQuery);
    const query = item.query || '';

    let url = item.url;

    if (url) {
      const queryParams = {
        eq: enteredQuery,
        etslf: Date.now() - this._lastFocused,
      };

      url = urlUtil.appendQueryString(url, queryParams);

      const type = item.type;
      const updatedStates = this.getNewQueryStates({
        type,
        query,
        index: item.resultIndex,
      });

      this.setState({
        focused: false,
        hoverItem: undefined,
        queryStates: updatedStates,
        text: query,
        query,
      });

      url += constructQueryStatesParams({ updatedStates });
      this.navigate(query, url);
    }
  };

  handleTypeaheadMouseOut = () => {
    this.setState({
      hoverItem: undefined,
    });
  };

  handleRemoveAll = (e) => {
    e.stopPropagation();
    // In unauth, clearing last token should not trigger a navigate
    this.clearAndFocusSearchBox();
  };

  handleBlur = () => {
    if (this.props.onBlur) {
      this.props.onBlur();
    }

    this.setState({ focused: false });
  };

  handleClick = () => {
    this.setState({ focused: true });
  };

  handleFocus = () => {
    trackRegisterAction('unauth.search_box.click.focus');
    this._lastFocused = Date.now();
    this.setState({ focused: true, cursorPosition: null });
  };

  handleKeyDown = (e) => {
    const hoverItem = this.state.hoverItem;
    switch (e.keyCode) {
      case CONST.KeyCode.ENTER:
        this.handleSubmit(e);
        break;
      case CONST.KeyCode.ESCAPE:
        this.handleBlur();
        break;
      case CONST.KeyCode.UP:
      case CONST.KeyCode.DOWN:
        e.preventDefault();
        const isUp = e.keyCode === CONST.KeyCode.UP;
        const emptySuggestions = this.state.emptySuggestions;
        const suggestions =
          !this.state.text && emptySuggestions && emptySuggestions
            ? emptySuggestions
            : this.state.typeaheadSuggestions;
        let nextIndex;
        if (hoverItem) {
          nextIndex = suggestions.indexOf(hoverItem) + (isUp ? -1 : 1);
        } else {
          nextIndex = isUp ? suggestions.length - 1 : 0;
        }
        this.handleTypeaheadItemHover(suggestions[nextIndex]);
        break;
      default:
        break;
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();

    if (this.state.hoverItem) {
      this.handleTypeaheadItemSelected(this.state.hoverItem, this.state.text);
    } else {
      this.handleTypedSubmission(e);
    }
  };

  handleTypeaheadSuggestionsChange = (typeaheadSuggestions) => {
    this.setState({ typeaheadSuggestions });
  };

  handleTypedSubmission = (e) => {
    // if currently hovering over a typeahead suggestion, use handleTypeaheadItemSelected
    const query = this.state.text.trim();
    if (query === '') {
      return;
    }

    const encodedQuery = encodeURIComponent(query);
    const scope = this.state.currentScope || 'pins'; // TODO(anniec): scope changes
    let url =
      this.state.currentBoost === 'recent_week'
        ? `/search/pins/recent/?q=${encodedQuery}`
        : `/search/${scope}/?q=${encodedQuery}`;

    const updatedStates = this.getNewQueryStates({ type: QUERY_TERM_STATE_TYPE.USER_INPUT, query });
    url += '&rs=typed';
    url += constructQueryStatesParams({ updatedStates });

    pubSub.instance.publish(
      PCONST.PUBSUB_CHANNEL_SITE,
      PCONST.PUBSUB_TOPIC_SEARCH_QUERY_UPDATED_FORCE_UPDATE,
      query,
    );

    this.setState({
      queryStates: updatedStates,
    });

    this.handleBlur();
    this.navigate(query, url);
  };

  handleTextChange = (newText) => {
    if (this.state.hoverItem) {
      this.setState({
        hoverItem: undefined,
        text: newText,
      });
    } else {
      this.setState({ text: newText });
    }
  };

  logSearchPinNavigateClick() {
    const eventDict = {
      component: PCONST.thrift.ComponentType.SEARCH_BOX,
      element: PCONST.thrift.ElementType.SEARCH_BOX_TEXT_INPUT,
      event_type: PCONST.thrift.EventType.CLICK,
      view_type: PCONST.thrift.ViewType.SEARCH,
      view_parameter: PCONST.thrift.ViewParameterType.SEARCH_PINS,
    };
    const _contextEvent = contextEvent.fromPlainObject(eventDict);
    contextLogger.getInstance().addEvent(_contextEvent);

    trackRegisterAction('unauth.search_box.search');
  }

  navigate(query, url) {
    this.logSearchPinNavigateClick();
    if (query && this.props.onQueryChange) {
      this.props.onQueryChange(query, url);
    } else {
      changeWindowLocation(url, false);
    }
  }

  // navigating using guided search
  navigateWithData = ({ query, queryStates, rs, changedQueryState }) => {
    let url = '/';
    const scope = 'pins';

    if (query) {
      url = `/search/${scope}/?q=${encodeURIComponent(query)}`;
      if (rs) {
        url += `&rs=${rs}`;
      }
      url += constructQueryStatesParams({ queryStates, changedQueryState });
    }
    this.navigate(query, url);
  };

  subscribers = [];

  updateEmptySuggestions = (emptySuggestions) => {
    this.setState({ emptySuggestions });
  };

  clearAndFocusSearchBox() {
    this.setState({
      text: '',
      query: '',
      focused: true,
    });
  }

  render() {
    const { className, hideRemoveAll } = this.props;

    const { cursorPosition, focused, hoverItem, query, text, windowHeight } = this.state;

    // class names
    const viewType = 'guided';
    const typeaheadClass = cx(className, viewType);
    const typeaheadContainerClass = cx('typeaheadField', typeaheadClass);
    const tokenizedClass = cx('tokenizedInput', viewType, 'typeaheadWithTitles');
    const inputWrapperClass = cx('tokenizedInputWrapper', { focused });
    const inputClassNames = [cx('Input', { field: className })];

    const placeholder =
      !focused || !query ? i18n._('Search for easy dinners, fashion, etc.') : null;

    // To calculate dropdown max height, grab form element after it's been rendered.
    // windowHeight will be undefined on initial render and set after component mounts.
    let maxDropdownHeight;
    if (windowHeight) {
      const dropdownTop = this._form.getBoundingClientRect().bottom;
      maxDropdownHeight = windowHeight - 14 - dropdownTop;
    }

    const showTypeahead = focused;

    let overlay;
    if (showTypeahead) {
      const overlayStyles = { ...styles.overlay, ...styles.typeaheadOverlay };
      overlay = <div style={overlayStyles} />;
    }

    return (
      /* eslint-disable jsx-a11y/interactive-supports-focus */
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
      <div style={styles.openSearchBoxWrapper}>
        <InlineStyle unsafeCSS={UnauthSearchBoxCSS()} />
        <div className="OpenSearchForm" style={styles.searchForm}>
          {overlay}
          <form
            name="search"
            onSubmit={this.handleSubmit}
            ref={(ref) => {
              this._form = ref;
            }}
          >
            <div className={typeaheadContainerClass} style={styles.typeaheadField}>
              <div
                className={inputWrapperClass}
                onClick={this.handleClick}
                role="search"
                style={styles.tokenizedInputWrapper}
              >
                <em />
                <div style={styles.searchIcon}>
                  <Icon
                    accessibilityLabel={i18n._('search', 'accessible label for search icon')}
                    icon="search"
                    size={20}
                  />
                </div>
                <div className={tokenizedClass} style={styles.tokenizedInput}>
                  <div className="scrollWrapper" style={styles.scrollWrapper}>
                    <ul className="tokensWrapper" style={styles.tokensWrapper}>
                      <li className={cx('tokenizedItem', 'inputToken')} style={styles.inputToken}>
                        <label>
                          <Box display="visuallyHidden">{i18n._('Search')}</Box>
                          <SearchBoxInput
                            autoComplete="off"
                            classNames={inputClassNames}
                            cursorPosition={focused ? cursorPosition : null}
                            focused={focused}
                            name="q"
                            onBlur={this.handleBlur}
                            onChange={this.handleTextChange}
                            onFocus={this.handleFocus}
                            onKeyDown={this.handleKeyDown}
                            placeholder={placeholder}
                            value={(hoverItem && hoverItem.label) || text}
                          />
                        </label>
                      </li>
                    </ul>
                  </div>
                  {!hideRemoveAll && query ? (
                    <a
                      className="removeAll"
                      onClick={this.handleRemoveAll}
                      role="button"
                      style={styles.removeAll}
                    >
                      <Icon
                        accessibilityLabel={i18n._('cancel', 'accessible label for cancel icon')}
                        icon="cancel"
                        inline
                        size={12}
                      />
                    </a>
                  ) : null}
                  {/* eslint-enable */}
                </div>
              </div>
              <SearchTypeahead
                className={typeaheadClass}
                focused={showTypeahead}
                hoverItem={this.state.hoverItem}
                maxHeight={maxDropdownHeight}
                onHover={this.handleTypeaheadItemHover}
                onTypeaheadItemSelected={this.handleTypeaheadItemSelected}
                onTypeaheadMouseOut={this.handleTypeaheadMouseOut}
                onTypeaheadSuggestionsChange={this.handleTypeaheadSuggestionsChange}
                ref={(ref) => {
                  this._typeahead = ref;
                }}
                typeaheadSuggestions={this.state.typeaheadSuggestions}
                updateEmptySuggestions={this.updateEmptySuggestions}
                value={text}
              />
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default SearchBox;
